import { useEffect, useRef, useState } from 'react';

interface Stat {
  value: string;
  numericValue: number;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  { value: '500+', numericValue: 500, suffix: '+', label: '服务企业' },
  { value: '98%', numericValue: 98, suffix: '%', label: '客户满意度' },
  { value: '10亿+', numericValue: 10, suffix: '亿+', label: '处理数据量' },
  { value: '24/7', numericValue: 24, suffix: '/7', label: '技术支持' },
];

function AnimatedNumber({ value, suffix, isVisible }: { value: number; suffix: string; isVisible: boolean }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 1500;
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value, isVisible]);

  return (
    <span>
      {displayValue}
      {suffix}
    </span>
  );
}

export default function StatsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#1c5bff]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#1c5bff]/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionTimingFunction: 'var(--ease-expo-out)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            数据见证实力
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            我们用实际成果证明价值，持续为客户创造卓越体验
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionTimingFunction: 'var(--ease-expo-out)',
                transitionDelay: `${index * 150}ms`,
              }}
            >
              <div className="relative inline-block">
                <span className="text-5xl md:text-6xl font-bold text-[#1c5bff]">
                  <AnimatedNumber
                    value={stat.numericValue}
                    suffix={stat.suffix}
                    isVisible={isVisible}
                  />
                </span>
                {/* Glow Effect */}
                <div className="absolute inset-0 blur-xl bg-[#1c5bff]/20 -z-10" />
              </div>
              <p className="mt-4 text-gray-400 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
